import React from 'react';
import {motion, AnimatePresence} from 'motion/react';
import {TRANSITIONS} from '@/config/animations';
import {awardEventActions, getSuperpowerByStep, getIconByStep} from '@/events/awardEvents';
import {useStore} from '@nanostores/react';
import {$progressStore} from '@/stores/progressStore';

interface ValidationError {
    path: string;
    expected: string;
    actual: any;
    message: string;
}

interface ValidationResult {
    success: boolean;
    taskId: string;
    errors?: ValidationError[];
    message?: string;
    hint?: string;
}

interface ValidationResultProps {
    result: ValidationResult | null;
    onClose?: () => void;
    onNextTask?: () => void;
}

const ValidationResultComponent: React.FC<ValidationResultProps> = ({
                                                                        result,
                                                                        onClose,
                                                                        onNextTask
                                                                    }) => {
    const progressState = useStore($progressStore);
    const hasShownAward = React.useRef(false);
    
    if (!result) return null;
    
    // Автоматически показываем AwardLayer при успешной валидации
    React.useEffect(() => {
        if (result.success && progressState.currentTask && !hasShownAward.current) {
            hasShownAward.current = true;
            
            // Создаем полную копию данных о завершенной задаче
            const completedTaskData = {
                level: progressState.currentStep,
                totalLevels: 5,
                completedTask: JSON.parse(JSON.stringify(progressState.currentTask)), // Глубокая копия
                newSuperpower: getSuperpowerByStep(progressState.currentStep),
                awardIcon: getIconByStep(progressState.currentStep)
            };
            
            // Отправляем событие для показа AwardLayer с зафиксированными данными
            awardEventActions.showAward(completedTaskData);
            // Сразу закрываем ValidationResult
            onClose?.();
        }
    }, [result.success, progressState.currentTask, progressState.currentStep]);

    // Показываем ValidationResult только при успехе
    // При ошибках используется ValidationErrorModal из TaskPanel
    if (!result.success) {
        return null;
    }

    return (
        <AnimatePresence>
            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                exit={{opacity: 0, y: -20}}
                transition={TRANSITIONS.default}
                style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    width: '400px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '8px',
                    padding: '20px',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '15px'
                }}>
                    <h3 style={{
                        margin: 0,
                        color: '#155724',
                        fontSize: '18px'
                    }}>
                        ✅ Задача решена!
                    </h3>
                    {onClose && (
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '18px',
                                cursor: 'pointer',
                                color: '#155724',
                            }}
                        >
                            ✕
                        </button>
                    )}
                </div>

                {result.message && (
                    <p style={{
                        margin: '0 0 15px 0',
                        color: '#155724',
                        fontSize: '14px'
                    }}>
                        {result.message}
                    </p>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default ValidationResultComponent;
